import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Zap, ShieldCheck, Download } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

export default function Home() {
  const [products, setProducts] = useState([]);
  useEffect(() => { api.get("/storefront/products").then((r) => setProducts(r.data.slice(0, 4))).catch(() => {}); }, []);

  return (
    <div>
      <section className="relative bg-[#2E1065] overflow-hidden">
        <div className="absolute inset-0 qru-hero-grid opacity-60" />
        <div className="absolute -right-32 -top-32 w-96 h-96 rounded-full bg-[#3B1F6E] blur-3xl opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <span className="inline-block bg-[#D97706] text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-6">Instant Digital Delivery</span>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]">
              Knowledge, <span className="text-[#D97706] italic">beautifully</span> delivered.
            </h1>
            <p className="mt-6 text-lg text-purple-100/80 max-w-lg leading-relaxed">
              A curated storefront of digital books and resources. Purchase once, download instantly, keep forever.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/shop" data-testid="hero-shop-button" className="inline-flex items-center gap-2 bg-[#D97706] hover:bg-[#B45309] text-white font-semibold px-7 py-3.5 rounded-full transition-colors">
                Browse the Shop <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/books" className="inline-flex items-center gap-2 border border-white/25 text-white hover:bg-white/10 font-semibold px-7 py-3.5 rounded-full transition-colors">
                View Books
              </Link>
            </div>
            <div className="mt-12 flex gap-8">
              {[[Zap, "Instant access"], [ShieldCheck, "Secure checkout"], [Download, "Yours forever"]].map(([Icon, label], i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-purple-100/70"><Icon className="w-4 h-4 text-[#D97706]" /> {label}</div>
              ))}
            </div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="hidden lg:block">
            <img src="/hero-qru.jpg" alt="QRU Online digital books and workbooks" className="rounded-2xl shadow-2xl w-full object-cover aspect-[4/5]" />
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-semibold text-[#0A0F1D] tracking-tight">Featured Releases</h2>
            <p className="text-slate-500 mt-2">Hand-picked titles ready to read today.</p>
          </div>
          <Link to="/shop" className="hidden sm:inline-flex items-center gap-1.5 text-[#3B1F6E] font-semibold hover:gap-2.5 transition-all">View all <ArrowRight className="w-4 h-4" /></Link>
        </div>
        {products.length === 0 ? (
          <p className="text-slate-400 text-center py-16">New titles are coming soon.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((p) => <ProductCard key={p.product_key} p={p} />)}
          </div>
        )}
      </section>
    </div>
  );
}
